import { useEffect, useRef } from 'react';
import { Shield } from 'lucide-react';
import { Button } from '../../ui/button';
import type { InvestmentProperty } from '../InvestmentFlow.types';
import { formatCurrency } from '../InvestmentFlow.utils';

interface ReviewStepProps {
  property: InvestmentProperty;
  investmentAmount: number;
  shares: number;
  onConfirm: () => void;
}

export function ReviewStep({ property, investmentAmount, shares, onConfirm }: ReviewStepProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-3xl md:text-4xl focus-visible:outline-none"
        >
          Review your investment
        </h1>
        <p className="text-lg text-muted-foreground">Make sure everything looks good</p>
      </div>

      {/* Investment summary */}
      <div className="rounded-xl border-2 border-border overflow-hidden">
        <div className="relative h-32 md:h-40">
          <img
            src={property.image}
            alt={property.title}
            loading="lazy"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" aria-hidden="true" />
          <div className="absolute bottom-4 left-4 right-4 text-white">
            <h2 className="text-xl font-medium mb-1">{property.title}</h2>
            <p className="text-sm opacity-90">{property.location}</p>
          </div>
        </div>

        <div className="p-6 bg-card">
          <dl className="divide-y divide-border">
            <div className="flex items-center justify-between py-3">
              <dt className="text-muted-foreground">Investment amount</dt>
              <dd className="text-2xl font-medium">{formatCurrency(investmentAmount)}</dd>
            </div>
            <div className="flex items-center justify-between py-3">
              <dt className="text-muted-foreground">Share price</dt>
              <dd className="font-medium">{formatCurrency(property.pricePerShare)} / share</dd>
            </div>
            <div className="flex items-center justify-between py-3">
              <dt className="text-muted-foreground">Number of shares</dt>
              <dd className="font-medium">{shares.toLocaleString()}</dd>
            </div>
            <div className="flex items-center justify-between py-3">
              <dt className="text-muted-foreground">Expected annual yield</dt>
              <dd className="font-medium text-success">{(property.annualYield * 100).toFixed(1)}%</dd>
            </div>
            <div className="flex items-center justify-between py-4">
              <dt className="font-medium">Total today</dt>
              <dd className="text-2xl font-medium">{formatCurrency(investmentAmount)}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Trust indicators — accurate claims only */}
      <div className="flex flex-wrap gap-4 justify-center text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-primary" aria-hidden="true" />
          <span>SEC-regulated offering</span>
        </div>
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-primary" aria-hidden="true" />
          <span>256-bit encryption</span>
        </div>
      </div>

      <Button onClick={onConfirm} className="w-full h-14 text-lg">
        Confirm and Choose Payment
      </Button>
    </div>
  );
}
